




import Vue, { PropType } from "vue";
import { ProjectStore } from "@/store/ProjectStore";
import { MessageViewParam } from "@/components/MessageView.vue";
import { messages } from "@/ts/const/Messages";
import { Err } from "@/ts/objects/Err";
import { emptySaveResult, SaveResult } from "@/ts/objects/editable/SaveResult";
import { EditableProjectLookback } from "@/ts/objects/project/editable/EditableProjectLookback";
import { NavigationGuardNext, Route } from "vue-router";
import { AppStateStore } from "@/store/AppStateStore";
import log from "loglevel";
import { PageLeaveService } from "@/ts/services/PageLeaveService";
import ProjectLookbackPure from "@/views/project/student/ProjectLookback/ProjectLookbackPure.vue";
import { ProjectRepository } from "@/ts/repositories/ProjectRepository";

export default Vue.extend({
  name: "ProjectLookback",
  components: { ProjectLookbackPure },
  props: {
    appStateStore: { type: Object as PropType<AppStateStore>, required: true },
    projectStore: { type: Object as PropType<ProjectStore>, required: true },
    projectRepository: { type: Object as PropType<ProjectRepository>, required: true },
  },
  created() {
    const vm = this;
    this.periodicSaverId = window.setInterval(() => vm.saveAll(false), 15000);
    this.pageLeaveService = new PageLeaveService({
      onLeaveStart: async () => {
        await this.saveAll(true);
      },
      requirementToLeave: async () => !this.needSave,
    });
    this.reloadData();
  },
  beforeRouteUpdate(to: Route, from: Route, next: NavigationGuardNext) {
    this.pageLeaveService!.tryLeave().then(ok => {
      if (!ok) {
        next(false);
        return;
      }
      this.reloadData();
      next();
    });
  },
  beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext) {
    this.pageLeaveService!.tryLeave().then(ok => {
      if (!ok) {
        next(false);
        return;
      }
      next();
    });
  },
  beforeDestroy() {
    clearInterval(this.periodicSaverId);
  },
  data(): {
    messageView: MessageViewParam | null;

    periodicSaverId: number | undefined;
    lookback: EditableProjectLookback | null;

    pageLeaveService: PageLeaveService | null;
  } {
    return {
      messageView: null,

      periodicSaverId: undefined,
      lookback: null,

      pageLeaveService: null,
    };
  },
  computed: {
    isAppUserGuardian(): boolean {
      return this.appStateStore.isAppUserGuardian;
    },
    needSave(): boolean {
      return this.lookback?.needSave() ?? false;
    },
  },
  methods: {
    async reloadData() {
      this.lookback = null;
      this.messageView = null;

      const studentUserId = this.appStateStore.studentOrGuardianState?.studentUserId() ?? null;
      if (studentUserId === null) {
        this.messageView = { message: messages.pleaseSelectStudent };
        return;
      }

      const project = await this.projectStore.project.getDataWithTimeout();
      if (project === null) {
        this.messageView = { message: messages.pleaseSelectProject };
        return;
      }

      if (!project.published) {
        // 児童生徒画面なので、非公開プロジェクトがここに来るはずはない、が、念のため。
        this.messageView = { message: messages.projectUnpublished };
        return;
      }

      const resp = await this.projectRepository.listEditableProjectLookbacks(
        project.projectId,
        studentUserId,
        false,
        true,
        this.appStateStore.isAppUserGuardian
      );
      if (resp instanceof Err || resp.length === 0) {
        log.error(`Error loading lookbacks: ${JSON.stringify(resp)}`);
        this.messageView = { message: messages.failedToLoadData };
        return;
      }

      this.lookback = resp[0];
      this.messageView = null;
    },
    async saveAll(force: boolean): Promise<SaveResult> {
      log.debug("SAVING!");
      return (await this.lookback?.saveAllChanges(force)) ?? emptySaveResult();
    },
  },
});
